import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {UserFlowStep} from '../../Facade/UserFlow/BaseUserFlow';
import {useUserFlowState} from '../Context/UserFlow/UserFlowContext';
import {BreadCrumbTrail} from './Common/BreadCrumbTrail/BreadCrumbTrail';
import {useAssistant} from '../Context/Assistant/AssistantContext';
import {UserFlowType} from '../../Facade/UserFlow/UserFlowFacade';
import {useExperience} from '../Experience/ExperienceProvider';
import {Theme, ThemeName} from '@taopix/taopix-design-system';
import classNames from 'classnames';
import {CoverMode} from './Step/CreateCoverStep';
import {AssistantNavigationProps} from './Assistant';

type AssistantNavProps = {
  children?: any;
} & AssistantNavigationProps;

export const AssistantNav = ({backButton, nextButton, onNext}: AssistantNavProps) => {
  const assistant = useAssistant();
  const userFlowState = useUserFlowState();
  const {experience} = useExperience();
  const {t, i18n} = useTranslation(['assistants']);
  const languageCode = i18n.language || localStorage.getItem('i18nextLng');

  const currentStep = userFlowState.getCurrentStep(UserFlowType.Assistant);

  const compareStepIndex = (step: string) => {
    // Current step index.
    const currentItemIndex = Array.from(breadCrumbItems[0].keys()).findIndex(
      item => currentStep === `${UserFlowType.Assistant}.${item}`
    );

    // Index of the item that was clicked.
    return (
      Array.from(breadCrumbItems[0].keys()).findIndex(item => step === item) - currentItemIndex
    );
  };

  const onClick = (event: React.MouseEvent, item: string): void => {
    if (!experience.WIZARD.navigation.breadcrumbTrail) {
      return;
    }

    const diff = compareStepIndex(item);
    // Allow going back to any step, only allow going forward 1 step.
    if (1 === diff) {
      onNext();
    } else if (diff < 0) {
      userFlowState.setCurrentStep(`${UserFlowType.Assistant}.${item}`);
    }
  };

  const breadCrumbItems = useMemo((): [
    Map<string, {label: string; onClick: (event: React.MouseEvent) => void; clickable: boolean}>,
    number
  ] => {
    if (!experience.WIZARD.navigation.breadcrumbTrail) {
      return [new Map(), 0];
    }

    const steps = userFlowState
      .getSteps(UserFlowType.Assistant)
      .filter(step => `${UserFlowType.Assistant}.${UserFlowStep.AssistantWelcome}` !== step)
      .map(step => (step as string).split('.')[1]);

    let selectedStepIndex = -1;
    let minActiveStepIndex = steps.length + 1;

    const mapItems: [
      string,
      {label: string; onClick: (event: React.MouseEvent) => void; clickable: boolean}
    ][] = steps
      .filter(item => {
        if (
          0 === assistant.state.coverItems[CoverMode.Front].pictureBox.length &&
          0 === Object.keys(assistant.state.coverItems[CoverMode.Front].textBox).length &&
          UserFlowStep.AssistantFrontCover === item
        ) {
          return false;
        }

        if (
          0 === assistant.state.coverItems[CoverMode.Back].pictureBox.length &&
          0 === Object.keys(assistant.state.coverItems[CoverMode.Back].textBox).length &&
          UserFlowStep.AssistantBackCover === item
        ) {
          return false;
        }

        if (assistant.state.useAllPictures && UserFlowStep.AssistantSmartSelection === item) {
          return false;
        }

        if (UserFlowStep.AssistantWelcome === item) {
          return false;
        }

        return UserFlowStep.None !== item;
      })
      .map((item, index) => {
        if (currentStep.split('.')[1] === item) {
          selectedStepIndex = index;

          if (nextButton.props.disabled) {
            // If the next button is disabled, then disable the breadcrumb item.
            minActiveStepIndex = index;
          }
        }
        return item;
      })
      .map((item, index) => {
        switch (item) {
          case UserFlowStep.AssistantManageAssets: {
            return [
              item,
              {
                label: t('*:str_LabelPictures'),
                onClick: event => {
                  onClick(event, item);
                },
                clickable: index < minActiveStepIndex,
              },
            ];
          }
          case UserFlowStep.AssistantFrontCover: {
            return [
              item,
              {
                label: t('*:str_LabelFrontCover'),
                onClick: event => {
                  onClick(event, item);
                },
                clickable: index < minActiveStepIndex,
              },
            ];
          }
          case UserFlowStep.AssistantBackCover: {
            return [
              item,
              {
                label: t('*:str_LabelBackCover'),
                onClick: event => {
                  onClick(event, item);
                },
                clickable: index < minActiveStepIndex,
              },
            ];
          }
          case UserFlowStep.AssistantSmartSelection: {
            return [
              item,
              {
                label: t('str_LabelSmartSelection'),
                onClick: event => {
                  if (selectedStepIndex > index) return;
                  onClick(event, item);
                },
                clickable: selectedStepIndex <= index && index < minActiveStepIndex,
              },
            ];
          }
          case UserFlowStep.AssistantPreview: {
            return [
              item,
              {
                label: t('*:str_ButtonPreview'),
                onClick: event => {
                  onClick(event, item);
                },
                clickable: index < minActiveStepIndex,
              },
            ];
          }
          default: {
            return [item, {label: '', onClick: () => {}, clickable: false}];
          }
        }
      });

    return [new Map(mapItems), selectedStepIndex];
  }, [
    languageCode,
    assistant.state.useAllPictures,
    experience.WIZARD.navigation.breadcrumbTrail,
    nextButton.props.disabled,
    currentStep,
  ]);

  const navClassNames = classNames(
    'flex',
    'px-lg',
    'h-[70rem]',
    'items-center',
    'justify-between',
    'border-themeBorderColour',
    'border-b-[max(theme(borderWidth.themeBorderSize),0px)]'
  );

  const breadCumbTrailItems = Array.from(breadCrumbItems[0].values());

  return (
    <Theme
      name={'tpx-theme-header'}
      tipLabel={'Header'}
      isRootTheme
      allowBorder={false}
      allowCorners={false}
      element={'nav'}
    >
      <Theme
        name={ThemeName.Container}
        allowBorder={false}
        allowCorners={false}
        className={navClassNames}
      >
        {backButton}
        {experience.WIZARD.navigation.breadcrumbTrail && breadCumbTrailItems.length > 1 && (
          <BreadCrumbTrail
            items={Array.from(breadCrumbItems[0].values())}
            selectedItemIndex={breadCrumbItems[1]}
          />
        )}
        {nextButton}
      </Theme>
    </Theme>
  );
};
